import { useLocation } from 'react-router-dom';

import { useAppSelector } from '@/store';
import { selectStaleUnpaidSession } from '@/store/slices/session';

import { ROUTE } from '../../routes';

import { useProductScore } from '../../hooks/useProductScore';
import type { LumioSignals } from '../../lib/lumioSignals';
import { gaugeTone } from '../../theme/tokens';

/**
 * The store-backed part of Lumio's nudge signals (the Product page adds its
 * inactive features at send time, see `lib/lumioSignals`), taken from the same
 * state the pages render from — never from displayed text, which changes with the
 * owner's language.
 *
 * Only areas backed by real data are reported. Product is (catalog + the brand's
 * purchases); the Acquisition and Operations gauges still draw on mock data, so
 * they are left out until they have a real source. The score is omitted until the
 * catalog has loaded: before that `useProductScore` reads 0 / Poor, which Lumio
 * would record as a collapse and later celebrate as a recovery.
 *
 * `setup_incomplete` is reported from Home only, which is what the backend rule
 * documents and expects (`nudges.py:141`). It drives a `nag` with escalating
 * backoff whose counter is bumped on every emit, so sending it from every route
 * let the setup reminder interrupt the owner anywhere and escalate its own
 * silence faster than intended.
 */
export const useLumioSignals = (): Omit<LumioSignals, 'inactive_features'> => {
  const product = useProductScore();
  const onHome = useLocation().pathname === ROUTE.home();
  const setupIncomplete = useAppSelector((state) => selectStaleUnpaidSession(state) !== null);
  return {
    area_scores: product.loading
      ? {}
      : { product: { score: product.score, band: gaugeTone(product.score), calibrating: product.calibrating } },
    setup_incomplete: onHome && setupIncomplete,
  };
};
